import React from 'react';
import { Plus } from 'lucide-react';
import { components, type GroupBase, type MenuProps } from 'react-select';

export type MenuStaticActionConfig = {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  'aria-label'?: string;
};

export function resolveMenuStaticAction(
  selectProps: Record<string, unknown> | null | undefined
): MenuStaticActionConfig | null {
  const raw = selectProps?.menuStaticAction;
  if (!raw || typeof raw !== 'object') return null;
  const action = raw as MenuStaticActionConfig;
  if (typeof action.label !== 'string' || typeof action.onClick !== 'function') return null;
  return action;
}

export function SelectMenuStaticActionFooter({ action }: { action: MenuStaticActionConfig }) {
  return (
    <div className="app-select__menu-static-action shrink-0 border-t border-gray-200 bg-white">
      <button
        type="button"
        disabled={action.disabled}
        aria-label={action['aria-label'] ?? action.label}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-blue-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          action.onClick();
        }}
      >
        {action.icon ?? <Plus className="h-4 w-4 shrink-0" aria-hidden />}
        <span>{action.label}</span>
      </button>
    </div>
  );
}

/**
 * react-select Menu wrapper: keeps MenuList scroll/infinite-scroll intact and pins an action row at the bottom.
 */
export function MenuWithStaticAction<Option, IsMulti extends boolean = false>(
  menuProps: MenuProps<Option, IsMulti, GroupBase<Option>>
) {
  const action = resolveMenuStaticAction(
    menuProps.selectProps as unknown as Record<string, unknown> | undefined
  );
  const menuClassName = [menuProps.className, action ? 'app-select-menu-with-static-action' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <components.Menu {...menuProps} className={menuClassName || undefined}>
      {menuProps.children}
      {action ? <SelectMenuStaticActionFooter action={action} /> : null}
    </components.Menu>
  );
}

/**
 * Merges a sticky menu action into an existing react-select `components` map (e.g. with PaginatedSelectMenuList).
 */
export function mergeComponentsWithMenuStaticAction(
  userComponents?: Record<string, unknown>,
  menuStaticAction?: MenuStaticActionConfig | null
): Record<string, unknown> {
  if (!menuStaticAction) return userComponents ?? {};
  return {
    ...(userComponents ?? {}),
    Menu: MenuWithStaticAction,
  };
}
